/* eslint-disable react-refresh/only-export-components */
import { useGetTableData } from "@/queries/data-center/data-center.api";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import { cn } from "@/utils/theme";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";

const formatNumberValue = (val: unknown): string => {
  const num = Number(val);
  if (isNaN(num)) return String(val ?? "");
  return num.toLocaleString("en-US", { maximumFractionDigits: 10 });
};

export const getDataTypeIcon = (dataType: string): React.ReactNode => {
  switch (dataType) {
    case "textarea":
      return <Icon type="far" name="paragraph" />;
    case "identity":
    case "passport_number":
    case "passport":
    case "dui_nit":
      return <Icon type="far" name="id-card" />;
    case "select":
      return <Icon type="far" name="list" />;
    case "file":
      return <Icon type="far" name="file-upload" />;
    case "switch":
      return <Icon type="far" name="toggle-on" />;
    case "string":
      return <Icon type="far" name="text" />;
    case "number":
      return <Icon type="far" name="hashtag" />;
    case "date":
    case "datetime":
      return <Icon type="far" name="calendar-clock" />;
    case "date_range":
    case "datetime_range":
      return <Icon type="far" name="calendars" />;
    case "paragraph":
      return <Icon type="far" name="paragraph" />;
    case "list":
      return <Icon type="far" name="list" />;
    case "email":
      return <Icon type="far" name="envelope" />;
    case "phone":
      return <Icon type="far" name="phone" />;
    case "files":
      return <Icon type="far" name="file-upload" />;
    default:
      return <Icon type="fas" name="text" />;
  }
};

type Fields =
  FormDetailResponse["steps"][number]["sections"][number]["fields"][number];

const columnHelper = createColumnHelper<Fields>();
export const tableColumns = (
  assigment: FormDetailResponse["form_assignments"][number] | undefined,
  showReasonModal: (reason: string) => void,
) => {
  return [
    columnHelper.accessor("name", {
      header: "Tipo de dato",
      meta: {
        className:
          "align-top w-[40px] min-w-[40px] max-w-[40px] lg:w-[353px] lg:min-w-[353px] lg:max-w-[353px] align-center",
      },
      cell: (info) => {
        if (!info.getValue()) {
          return <p>&nbsp;</p>;
        }
        const field = info.row.original;

        return (
          <div className="w-full flex items-center gap-4">
            {getDataTypeIcon(field.type)}
            <div className="hidden lg:block w-full overflow-x-hidden">
              <p className="w-full text-body-loud font-semibold truncate text-size-body max-w-[90%]">
                {field.label}
              </p>
              <p className="text-info text-xs truncate">
                {field.required ? "Obligatorio" : "Opcional"}
              </p>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("filled_fields", {
      header: "Valor",
      meta: {
        className:
          "w-full overflow-hidden !whitespace-normal break-all align-center",
      },
      cell: (info) => {
        const fieldArr = info.getValue();
        if (!fieldArr || fieldArr.length === 0) {
          return <p>&nbsp;</p>;
        }

        const obj = info.row.original;

        // Handle interoperability fields - show all filled_fields
        if (obj.type === "interoperability") {
          const uniqueFieldsCount = new Set(fieldArr.map((f) => f.label)).size;
          const lastFields = fieldArr.slice(-uniqueFieldsCount);
          return (
            <div className="flex flex-col gap-2">
              {lastFields.map((filledField, index) => (
                <div
                  key={`${filledField.id}-${index}`}
                  className="flex flex-col"
                >
                  <p className="text-xs text-info font-semibold">
                    {filledField.label}:
                  </p>
                  <p className="break-normal whitespace-pre-wrap text-size-body">
                    {(filledField.value || "").toString()}
                  </p>
                </div>
              ))}
            </div>
          );
        }

        const filledFieldCount = fieldArr.length - 1;
        const value = fieldArr[filledFieldCount]?.value;

        if (
          Object.prototype.hasOwnProperty.call(value || {}, "start") &&
          Object.prototype.hasOwnProperty.call(value || {}, "end")
        ) {
          const { start, end } = value as { start: string; end: string };
          return (
            <p className="break-normal whitespace-pre-wrap text-size-body">
              {start}
              {" -> "}
              {end}
            </p>
          );
        }

        if (obj.type === "dui_nit" && obj.config.allow_select_document_type) {
          const duiNITValue = value as unknown as {
            type: string;
            value: string;
          };

          if (!duiNITValue) {
            return <p>&nbsp;</p>;
          }

          if (!duiNITValue.value) {
            return <p>&nbsp;</p>;
          }

          return (
            <p className="break-all whitespace-pre-wrap text-size-body">
              {duiNITValue.value} ({duiNITValue.type.toUpperCase()})
            </p>
          );
        }

        if (obj.type === "file") {
          const files = fieldArr[filledFieldCount].files || [];
          return (
            <ul className="flex gap-2 flex-wrap">
              {files.map((f) => {
                return (
                  <Badge
                    variant="neutral"
                    className="max-w-70 cursor-pointer"
                    onClick={() => {
                      window.open(f.url, "_blank", "noopener,noreferrer");
                    }}
                  >
                    <p className="truncate">{f.file_name}</p>
                    <Icon
                      type="fas"
                      name="file"
                      size="xs"
                      className="shrink-0"
                    />
                  </Badge>
                );
              })}
            </ul>
          );
        }

        if (obj.type === "select" && obj.config.options_type === "manual") {
          if (obj.config.multiple) {
            const values = value as Array<string>;
            const result = values.map((val) => {
              const option = obj.config.options.find(
                (o: { value: string }) => o.value === val,
              );
              return option ? option.label : null;
            });
            return result.join(", ");
          }
          const option = obj.config.options.find(
            (o: { value: string }) => o.value === value,
          );

          if (!option) {
            return <p>&nbsp;</p>;
          }
          return option.label;
        }

        if (
          obj.type === "select" &&
          obj.config.options_type === "database" &&
          obj.config.multiple &&
          Array.isArray(value)
        ) {
          return (value as Array<string>).map((val) => {
            const isLast =
              val ===
              (value as Array<string>)[(value as Array<string>).length - 1];
            return (
              <span key={val}>
                <DataCenterValue
                  table_name={obj.config.data_center_table}
                  column_name={obj.config.data_center_column}
                  table_source={obj.config.source}
                  value={val}
                />
                {!isLast && ", "}
              </span>
            );
          });
        }

        if (
          obj.type === "select" &&
          obj.config.options_type === "database" &&
          !obj.config.multiple
        ) {
          return (
            <DataCenterValue
              table_name={obj.config.data_center_table}
              column_name={obj.config.data_center_column}
              table_source={obj.config.source}
              value={value as string}
            />
          );
        }

        if (Array.isArray(value)) {
          return (
            <div className="flex gap-2 flex-wrap p-3">
              {(value as Array<string>).map((fileName, index) => (
                <Badge
                  key={"file_" + index}
                  size="sm"
                  shape="pill"
                  variant="neutral"
                  className="text-size-body-sm"
                >
                  {fileName}
                  <Icon type="fas" name="file" className="text-neutral-quiet" />
                </Badge>
              ))}
            </div>
          );
        }

        // Manejar valores booleanos
        if (typeof value === "boolean") {
          return (
            <p className="break-normal whitespace-pre-wrap text-size-body">
              {value ? "Sí" : "No"}
            </p>
          );
        }

        // Formatear campos numéricos con separador de miles
        if (obj.type === "number") {
          return (
            <p className="break-normal whitespace-pre-wrap text-size-body">
              {formatNumberValue(value)}
            </p>
          );
        }

        return (
          <p className="break-normal whitespace-pre-wrap text-size-body">
            {(value || "").toString()}
          </p>
        );
      },
    }),
    ...(assigment?.revisions && assigment.revisions.length > 0
      ? [
          columnHelper.display({
            id: "correction",
            header: "Corrección",
            meta: {
              className: "w-[48px] border-transparent border-x-2 align-center",
            },
            cell: ({ row }) => {
              const filled_field =
                row.original.filled_fields?.[
                  row.original.filled_fields.length - 1
                ];
              const correction = filled_field?.correction;
              if (!correction) {
                return <p>&nbsp;</p>;
              }
              return (
                <div className="relative">
                  {correction?.status === "DENIED" && (
                    <Badge
                      size="xs"
                      variant="danger"
                      fill="solid"
                      className="rounded-full size-5 flex items-center justify-center absolute -top-3 -right-2"
                      onClick={() => {
                        showReasonModal(
                          correction?.comments?.[0]?.text || "RAZON",
                        );
                      }}
                    >
                      <Icon
                        name="comment"
                        type="fas"
                        className="text-2xs text-on-solid"
                      />
                    </Badge>
                  )}
                  <Badge
                    shape="square"
                    size="sm"
                    className="size-6 flex items-center justify-center rounded-lg"
                    variant={
                      correction?.status === "APPROVED" ? "success" : "danger"
                    }
                  >
                    <Icon
                      name={
                        correction.status === "APPROVED" ? "check" : "times"
                      }
                      type="fas"
                      className={cn(
                        "text-xs",
                        correction?.status === "APPROVED" &&
                          "text-success-quiet",
                        correction?.status === "DENIED" && "text-danger-quiet",
                      )}
                    />
                  </Badge>
                </div>
              );
            },
          }),
        ]
      : []),
  ] as ColumnDef<Fields>[];
};

const DataCenterValue = ({
  value,
  table_name,
  column_name,
  table_source,
}: {
  table_name: string;
  column_name: string;
  table_source: string;
  value?: string | number | string[];
}) => {
  const { data } = useGetTableData(table_name!, table_source, 1, [
    {
      name: "id",
      value: value as string,
      operator: "EQUALS",
      type: "STRING",
    },
  ]);
  if (!data) return "Cargando...";

  const record = data?.data?.data?.[0];
  if (record) {
    return (
      <span className="text-body">
        {record[column_name as keyof typeof record] as string}
      </span>
    );
  }

  return <span className="text-body">{"—"}</span>;
};
